import { formatDate, isDefined } from '@/composables/utils'
import { EventCategory } from '@/enums/EventCategory'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import type { ICalendarRange } from '@/interfaces/ICalendarRange'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import type { ITableHeader } from '@/interfaces/ITableHeader'
import { EventRecord } from '@/models/EventRecord'
import type { PeopleRecord } from '@/models/PeopleRecord'
import { useSnackbarStore } from '@/stores/ui/SnackbarStore'
import axios from 'axios'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { TableStoreFactory } from './TableStoreFactory'

const ENDPOINT = 'self-scheduling'

export const useSelfSchedulerStore = defineStore('selfScheduling', () => {
  // STORES, IMPORTS, & COMPOSABLES
  const i18n = useI18n({ useScope: 'global' })
  const eventsStore = TableStoreFactory.get(TableStoreId.Events) as IEventsStore
  const snackbarStore = useSnackbarStore()
  const router = useRouter()

  function _getHTTPHeaders() {
    const headers = {
      'x-dataset-id': datasetId.value
    }
    return headers
  }

  const http = axios.create({
    baseURL: APP_ENV.VITE_APP_FITPRO_HOST,
    timeout: 60000
  })

  // REACTIVE VARIABLES
  const category = ref<EventCategory>()
  const datasetId = ref<EventCategory>()
  const headers = ref<Array<ITableHeader>>([
    {
      title: 'fields.events.location',
      key: 'location',
      align: 'start',
      headerProps: { searchField: true }
    },
    {
      title: 'fields.events.organizer',
      key: 'organizerId',
      align: 'start',
      value: (item: EventRecord) => {
        return `${item.organizerLastName}, ${item.organizerFirstName}`
      }
    },
    {
      title: 'fields.events.dtStart',
      key: 'dtStart',
      nowrap: true,
      align: 'start',
      value: (item: EventRecord) => {
        return isDefined(item.dtStart) ? formatDate(item.dtStart, true) : '-'
      }
    }
  ])
  const key = ref<string>()
  const locations = ref<Array<EventRecord>>([])
  const slots = ref<Array<EventRecord>>([])
  const person = ref<PeopleRecord | null>()
  const range = ref<ICalendarRange>()
  const showEditDialog = ref<boolean>(false)
  const selectedItem = ref<EventRecord | null>(null)
  const selectedLocation = ref<string | null>(null)

  // COMPUTED
  const appointments = computed(() => {
    return slots.value.filter((event) => {
      return event.attendeeId === person.value?.id
    })
  })

  const categoryTitle = computed(() => {
    return eventsStore.getCategoryTitle(category.value) || ''
  })

  const locationValues = computed((): Array<ISimpleOption> => {
    const options: Array<ISimpleOption> = locations.value.map((item) => {
      return { title: item.location, value: item.location }
    })
    options.unshift({ title: i18n.t('labels.all'), value: null })
    return options
  })

  const totalOpenSlots = computed((): number => {
    const openSlots = slots.value.filter((slot) => {
      return slot.attendeeId === null
    })
    return openSlots.length
  })

  // FUNCTIONS - PUBLIC
  const authenticateAndLoad = async () => {
    category.value = Number(router.currentRoute.value.query.category)
    datasetId.value = Number(router.currentRoute.value.query.datasetid)
    key.value = router.currentRoute.value.query.key as string

    // Must have a valid scheduling key.
    if (!isDefined(key.value) || key.value?.length !== 36) {
      return false
    }

    // Must have a valid datasetid.
    if (isNaN(datasetId.value)) {
      return false
    }

    // Must have a valid category.
    if (isNaN(category.value) || category.value < 1 || category.value > 3) {
      return false
    }

    await loadPerson()

    // Must have vlid person corrsponding to the scheduling key provided.
    if (!isDefined(person.value)) {
      return false
    }

    await loadLocations()
    return true
  }

  const cancelAppointment = async (appointment: EventRecord): Promise<boolean> => {
    try {
      const url: string = `${ENDPOINT}/cancel`
      const payload = {
        key: key.value,
        eventId: appointment.id
      }
      await http.post(url, payload, { headers: _getHTTPHeaders() })
      await loadSlots()
      return true
    } catch (error: any) {
      snackbarStore.addAxiosMessage(ENDPOINT, error)
      return false
    }
  }

  const loadLocations = async (): Promise<void> => {
    const url = `${ENDPOINT}/get-locations`
    try {
      const payload = {
        key: key.value,
        category: category.value,
        dtStart: dayjs().format('YYYY-MM-DD')
      }
      const response = await http.post(url, payload, { headers: _getHTTPHeaders() })
      locations.value = response.data
    } catch (error: any) {
      person.value = null
    }
  }

  const loadPerson = async (): Promise<void> => {
    const url = `${ENDPOINT}/get-person`
    try {
      const payload = { key: key.value }
      const response = await http.post(url, payload, { headers: _getHTTPHeaders() })
      person.value = response.data
    } catch (error: any) {
      person.value = null
    }
  }

  const loadSlots = async (newRange?: ICalendarRange): Promise<void> => {
    if (newRange) {
      range.value = newRange
    }
    if (!range.value) {
      slots.value = []
      return
    }
    try {
      const url: string = `${ENDPOINT}/get-slots`
      const dtStart = new Date(range.value.start).toISOString()
      const dtEnd = new Date(range.value.end).toISOString()
      const now = new Date().toISOString()

      const payload = {
        key: key.value,
        category: category.value,
        dtStart,
        dtEnd,
        now,
        location: selectedLocation.value
      }

      const response = await http.post(url, payload, { headers: _getHTTPHeaders() })
      slots.value = response.data?.results ?? []
    } catch (error: any) {
      snackbarStore.addAxiosMessage(ENDPOINT, error)
      slots.value = []
    }
  }

  const signup = async (key: string, eventId: number): Promise<boolean> => {
    try {
      const url: string = `${ENDPOINT}/signup`
      const payload = {
        key,
        eventId
      }
      await http.post(url, payload, { headers: _getHTTPHeaders() })
      await loadSlots()
      return true
    } catch (error: any) {
      snackbarStore.addAxiosMessage(ENDPOINT, error)
      return false
    }
  }

  return {
    appointments,
    category,
    categoryTitle,
    datasetId,
    headers,
    key,
    locationValues,
    totalOpenSlots,
    slots,
    person,
    range,
    selectedItem,
    selectedLocation,
    showEditDialog,
    cancelAppointment,
    loadSlots,
    loadPerson,
    authenticateAndLoad,
    signup
  }
})
